import * as Sentry from '@sentry/browser';

/**
 * How long a request may hold a slot before the watchdog takes it back.
 *
 * Nothing that runs through this queue should legitimately take five minutes:
 * a tag read is a handful of 1 KB range requests, a duration probe is one
 * metadata load, a temp link is one API call. A slot held longer than that is
 * held by a promise that will never settle - and it happens. A tab that Edge
 * put to sleep for two hours came back with all three slots held, no XHR and
 * no audio element left alive to settle them: the browser had torn the
 * requests down without firing anything. With no watchdog the queue was dead
 * for the life of the page, every refetch after it silently joined the
 * waiting list, and the Processing border stayed on for hours.
 *
 * The one thing this is *not* is a network timeout. The request keeps running
 * if it still is; only the slot and the caller's promise are given up.
 */
export const defaultSlotTimeoutMs = 5 * 60 * 1000;

export interface QueueOptions {
  /** What to call the request in the log when the watchdog gives up on it. */
  label?: string;
  /**
   * Overrides the queue's slot timeout for a request that is legitimately
   * long - a podcast save that waits on the backend, a large playlist sync.
   * Infinity leaves the request unwatched.
   */
  timeoutMs?: number;
}

/** What a caller gets when the watchdog gave its request's slot away. */
export class ReqQueueTimeoutError extends Error {
  constructor(
    readonly label: string,
    readonly heldMs: number,
  ) {
    super(`${label} did not finish in ${Math.round(heldMs / 1000)}s and was given up on`);
    this.name = 'ReqQueueTimeoutError';
  }
}

export const reqQueueCreator = function (
  setActive: () => void,
  disableActive: () => void,
  maxConcurrent = 3,
  slotTimeoutMs = defaultSlotTimeoutMs,
) {
  let reqCount = 0;
  const queueArray = new Array<() => void>();

  /**
   * Start the next waiting request in this slot, or give the slot up.
   */
  function release() {
    if (queueArray.length) {
      const queuedItm = queueArray.pop()!;

      setTimeout(queuedItm, 0);
    } else {
      reqCount--;
    }

    if (reqCount === 0) {
      disableActive();
    }
  }

  /**
   * Run one request in a slot it already holds. The slot is released exactly
   * once, whichever of the request settling or the watchdog firing comes
   * first; whatever comes second is ignored.
   */
  function run<T>(
    req: () => Promise<T>,
    options: QueueOptions,
    resolve: (value: T) => void,
    reject: (reason: unknown) => void,
  ) {
    const label = options.label || req.name || 'request';
    const timeoutMs = options.timeoutMs ?? slotTimeoutMs;
    const startedAt = Date.now();
    let done = false;

    function finish(settle: () => void) {
      if (done) {
        return;
      }

      done = true;
      clearTimeout(watchdog);
      release();
      settle();
    }

    const watchdog = Number.isFinite(timeoutMs)
      ? setTimeout(function slotWatchdog() {
          const heldMs = Date.now() - startedAt;

          // warn survives disableLoggingInProduction; debug and info do not.
          console.warn(
            'Request queue: giving up on',
            label,
            'after',
            Math.round(heldMs / 1000),
            'seconds - it never settled. Its slot goes to the next request;',
            queueArray.length,
            'waiting.',
          );

          Sentry.withScope((scope) => {
            scope.setExtra('label', label);
            scope.setExtra('heldMs', heldMs);
            scope.setExtra('waiting', queueArray.length);
            Sentry.captureMessage(
              'Request queue slot released by watchdog',
              Sentry.Severity.Warning,
            );
          });

          finish(() => reject(new ReqQueueTimeoutError(label, heldMs)));
        }, timeoutMs)
      : undefined;

    // A request that throws before returning a promise used to keep its slot
    // for ever: the direct path had already counted it, and the queued path
    // threw inside a timer where nobody was listening.
    let promise: Promise<T>;

    try {
      promise = Promise.resolve(req());
    } catch (err) {
      promise = Promise.reject(err);
    }

    promise.then(
      (value) => finish(() => resolve(value)),
      (err) => finish(() => reject(err)),
    );
  }

  return {
    queue<T>(req: () => Promise<T>, options: QueueOptions = {}): Promise<T> {
      return new Promise<T>(function (resolve, reject) {
        if (reqCount < maxConcurrent) {
          // Transition from inactive to active
          if (reqCount === 0) {
            setActive();
          }

          reqCount++;
          run(req, options, resolve, reject);
        } else {
          queueArray.push(() => run(req, options, resolve, reject));
        }
      });
    },
  };
};
